import {
  FeatureFlagRolloutType,
  FeatureFlagScope,
  FeatureFlagType,
  type FeatureFlag,
  type FeatureFlagVariant,
} from './types';

/**
 * Client-side mirror of osprey-api's `normalizeRolloutFields`
 * (src/domain/feature-flags/feature-flags.service.ts).
 *
 * Every rule here is a 400 on the server. Mirroring them lets the form say what
 * is wrong next to the field instead of surfacing a bare toast — but the server
 * stays authoritative: nothing here is a substitute for its check, and anything
 * it rejects still reaches the user through the normal error path.
 */
export function validateFlagDraft(draft: {
  key?: string;
  type: FeatureFlagType;
  scope: FeatureFlagScope;
  scopeValue?: string;
  rolloutType: FeatureFlagRolloutType;
  rolloutPercentage?: number | null;
  allowListMerchantIds?: string[];
  variants?: FeatureFlagVariant[];
  /** Create validates `key`; update never sends it (it is part of the identity). */
  requireKey?: boolean;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (draft.requireKey && !draft.key?.trim()) {
    errors.key = 'A key is required.';
  }

  // GLOBAL forces scopeValue to '' server-side, so an entered value would be
  // silently dropped rather than rejected — don't ask for one.
  if (draft.scope !== FeatureFlagScope.GLOBAL && !draft.scopeValue?.trim()) {
    errors.scopeValue = `A ${draft.scope === FeatureFlagScope.SOLUTION ? 'solution slug' : 'merchant id'} is required when scope is ${draft.scope}.`;
  }

  // Non-obvious server rule: a MERCHANT-scoped row already names one merchant,
  // so percentage/allow-list on top of it is rejected outright.
  if (draft.scope === FeatureFlagScope.MERCHANT && draft.rolloutType !== FeatureFlagRolloutType.ALL) {
    errors.rolloutType = 'MERCHANT-scoped flags must roll out to ALL.';
  }

  if (draft.rolloutType === FeatureFlagRolloutType.PERCENTAGE) {
    const pct = draft.rolloutPercentage;
    if (pct === undefined || pct === null || Number.isNaN(pct)) {
      errors.rolloutPercentage = 'A percentage is required for a PERCENTAGE rollout.';
    } else if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
      errors.rolloutPercentage = 'Enter a whole number between 0 and 100.';
    }
  }

  if (
    draft.rolloutType === FeatureFlagRolloutType.ALLOW_LIST &&
    !draft.allowListMerchantIds?.length
  ) {
    errors.allowListMerchantIds = 'List at least one merchant id for an ALLOW_LIST rollout.';
  }

  if (draft.type === FeatureFlagType.MULTIVARIATE) {
    const variants = draft.variants ?? [];
    if (!variants.length) {
      errors.variants = 'A multivariate flag needs at least one variant.';
    } else if (variants.some((variant) => !variant.key.trim())) {
      errors.variants = 'Every variant needs a key.';
    } else if (variants.every((variant) => variant.weight <= 0)) {
      // The server checks `every(weight <= 0)`, not `some` — a single positive
      // weight is enough, so don't invent a stricter rule than it enforces.
      errors.variants = 'At least one variant needs a weight above zero.';
    }
  }

  return errors;
}

/**
 * One-line answer to "who actually gets this?".
 *
 * Spells out the two rollout types that silently evaluate to `false` without a
 * merchant context: the server's `evaluate` requires `dto.merchantId` for both
 * PERCENTAGE and ALLOW_LIST, so a flag configured either way returns disabled
 * for a solution-only or anonymous evaluation no matter how it is set up.
 */
export function describeRollout(flag: Pick<FeatureFlag, 'rolloutType' | 'rolloutPercentage' | 'allowListMerchantIds'>): string {
  switch (flag.rolloutType) {
    case FeatureFlagRolloutType.PERCENTAGE:
      return `${flag.rolloutPercentage ?? 0}% of merchants (needs a merchant context)`;
    case FeatureFlagRolloutType.ALLOW_LIST: {
      const count = flag.allowListMerchantIds.length;
      return `${count} listed merchant${count === 1 ? '' : 's'} (needs a merchant context)`;
    }
    default:
      return 'Everyone in scope';
  }
}

/** Human label for where a row applies. GLOBAL has no scopeValue by construction. */
export function describeScope(flag: Pick<FeatureFlag, 'scope' | 'scopeValue'>): string {
  return flag.scope === FeatureFlagScope.GLOBAL ? 'Global' : `${flag.scope} · ${flag.scopeValue}`;
}

/**
 * Share of traffic a variant takes, as a percentage of total weight.
 *
 * The server picks by weighted random over the raw weights, so they need not sum
 * to 100 — showing the normalised share is the only way the editor reflects what
 * will actually happen. Zero total weight is possible mid-edit; report 0 rather
 * than dividing by it.
 */
export function variantShare(variants: FeatureFlagVariant[], index: number): number {
  const total = variants.reduce((sum, variant) => sum + Math.max(0, variant.weight), 0);
  if (total <= 0) return 0;
  return Math.round((Math.max(0, variants[index].weight) / total) * 1000) / 10;
}
